"use client";

import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { FAQ } from "@/features/analysis/services/get-symbol-faq";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

interface FAQItemProps {
  lang: Lang;
  symbol: string;
  faq: FAQ;
}

export const FAQItem = ({ lang, symbol, faq }: FAQItemProps) => {
  const { t } = getTranslations(lang);
  const [isOpen, setIsOpen] = useState(false);
  const ToggleIcon = isOpen ? MinusIcon : PlusIcon;
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const updateHeight = () => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  };

  const handleToggleClick = () => {
    setIsOpen((open) => !open);
    updateHeight();
  };

  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        updateHeight();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  const isShow = faq.values.every((item) => item !== null);
  if (!isShow) return null;

  const faqData = t.analysis.faq[faq.key];
  if (!faqData) return null;

  return (
    <li>
      <div
        className={twMerge(
          "flex cursor-pointer items-center gap-5 py-5 duration-300 lg:py-7",
          isOpen && "!pb-4"
        )}
        onClick={handleToggleClick}
      >
        <div className="flex-1">
          <h3 className="font-medium">{faqData.title({ symbol })}</h3>
        </div>
        <ToggleIcon className="h-5 w-5" />
      </div>
      <div
        className="overflow-hidden duration-300"
        style={{
          height: isOpen ? contentHeight : 0,
        }}
      >
        <p ref={contentRef} className="pb-4 text-zinc-600">
          {faqData.content(faq.values as (string | number)[])}
        </p>
      </div>
    </li>
  );
};
